// format.ts — the numeric and temporal vocabulary every docdaemon page shares.
// Kept out of api.ts because none of it is about the wire format: these are the
// decisions about how a sha, an elapsed time, a token count and a fraction of a
// cent are *read*, and a status card, a run table and a cost breakdown all have
// to make them the same way or the same number looks like two.

// EM_DASH stands in wherever a field is legitimately absent — a run that has
// not finished, a repo that has never been indexed. An empty cell reads as a
// rendering bug; a dash reads as "there is nothing here yet", which is the
// truth being reported.
export const EM_DASH = '—';

// shortSha is the 7 characters git itself abbreviates to. Anything shorter
// collides in a busy repository, and the full 40 is a column of noise.
export function shortSha(sha: string): string {
  if (!sha) return EM_DASH;
  return sha.slice(0, 7);
}

// relativeTime says how long ago, in the coarsest unit that is still true: a
// run from three weeks back does not need its hours, and one from ninety
// seconds ago does not want them rounded to "0h".
export function relativeTime(iso: string | null | undefined): string {
  if (!iso) return EM_DASH;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return EM_DASH;
  const seconds = (Date.now() - then) / 1000;
  // A clock skew of a few seconds between the pod and the browser is normal,
  // and "in 3 seconds" for something that already happened reads as a bug.
  if (seconds < 45) return 'just now';
  const minutes = seconds / 60;
  if (minutes < 60) return `${Math.round(minutes)}m ago`;
  const hours = minutes / 60;
  if (hours < 24) return `${Math.round(hours)}h ago`;
  const days = hours / 24;
  if (days < 30) return `${Math.round(days)}d ago`;
  return new Date(iso).toLocaleDateString();
}

// absoluteTime is the exact moment, for the `title` of anything relativeTime
// rounded. The pairing is the point: the page reads in relative terms and
// answers "when exactly?" on hover rather than spending a column on it.
export function absoluteTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
}

// formatDuration renders a run's elapsed wall time. Jobs are LLM calls with a
// ten-minute ceiling each, so a run spans seconds to hours.
export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return EM_DASH;
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

// elapsed is a run's duration from its two timestamps, measuring an unfinished
// run against now — which is what makes a running row's clock tick rather than
// sit at a dash until it lands.
export function elapsed(startedAt: string, finishedAt: string | null): string {
  const start = new Date(startedAt).getTime();
  if (Number.isNaN(start)) return EM_DASH;
  const end = finishedAt ? new Date(finishedAt).getTime() : Date.now();
  if (Number.isNaN(end)) return EM_DASH;
  return formatDuration(end - start);
}

// formatCount abbreviates token counts, which run to millions in a single
// bootstrap of a large repository. Three significant figures: the difference
// between 1.2M and 1.25M tokens is real money at these volumes, and the
// difference between 1.25M and 1.2534M is not.
export function formatCount(n: number): string {
  if (!Number.isFinite(n)) return EM_DASH;
  if (Math.abs(n) < 1000) return String(Math.round(n));
  if (Math.abs(n) < 1_000_000) return `${(n / 1000).toFixed(1)}k`;
  return `${(n / 1_000_000).toFixed(2)}M`;
}

// formatUsd keeps the significant digits of a small number rather than
// rounding a job's cost to $0.00. A single file summary against the local
// model costs a fraction of a cent, and a page of $0.00 rows would say the
// service is free — which is exactly the belief the cost page exists to
// correct. Anything at a dollar or more takes the usual two decimals.
export function formatUsd(usd: number): string {
  if (!Number.isFinite(usd)) return EM_DASH;
  if (usd === 0) return '$0.00';
  if (Math.abs(usd) < 0.01) return `$${usd.toFixed(5)}`;
  if (Math.abs(usd) < 1) return `$${usd.toFixed(4)}`;
  return `$${usd.toFixed(2)}`;
}

// formatTps renders a token-per-second rate.
//
// One decimal below ten, none above. A fleet generating 240 tok/s does not
// gain anything from ".3", and a CPU-bound model doing 2.4 tok/s reads as a
// flat "2" without it — which is the difference between "slow" and "stopped"
// at exactly the moment somebody is looking to tell those apart.
export function formatTps(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '0';
  if (n < 10) return n.toFixed(1);
  return formatCount(Math.round(n));
}

// commitSubject is a commit message's first line. The body of docdaemon's own
// commits lists every path it rewrote, which is worth reading in gitea and
// never worth putting in a table cell.
export function commitSubject(message: string): string {
  const first = message.split('\n', 1)[0].trim();
  return first || EM_DASH;
}

// percent is the share of a whole, guarding the zero denominator that every
// one of these ratios has on a fresh install (no jobs, no repos, no spend).
export function percent(part: number, whole: number): number {
  if (!whole) return 0;
  return Math.round((part / whole) * 100);
}
